// map-engine.jsx — procedural city-map generator + poster renderer
// Exports to window: MAP, generateCity, THEMES, MapPoster
(function () {
  const MAP_SIZE = 2400;

  // --- seeded RNG ---
  function hashString(s) {
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return h >>> 0;
  }
  function mulberry32(a) {
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      let t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // wavy polyline between two points with perpendicular jitter
  function wavy(x1, y1, x2, y2, rng, amp, segs) {
    segs = segs || 3;
    const dx = x2 - x1, dy = y2 - y1, len = Math.hypot(dx, dy) || 1;
    const nx = -dy / len, ny = dx / len;
    const pts = [];
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      const off = (i === 0 || i === segs) ? 0 : (rng() - 0.5) * amp;
      pts.push([x1 + dx * t + nx * off, y1 + dy * t + ny * off]);
    }
    return pts;
  }
  const toPts = (a) => a.map((p) => p[0].toFixed(1) + "," + p[1].toFixed(1)).join(" ");

  function generateCity(seedStr) {
    const rng = mulberry32(hashString(seedStr || "anywhere"));
    const S = MAP_SIZE, pad = 500;
    const roads = { minor: [], major: [], highway: [] };
    const parks = [];
    const rivers = [];
    const lakes = [];

    const minorGap = 78 + rng() * 46;          // block size
    const majorEvery = 3 + Math.floor(rng() * 2);
    const jit = minorGap * 0.22;
    const rot = (rng() - 0.5) * 0.42;           // global rotation (radians)

    // vertical streets
    let vi = 0;
    for (let x = -pad; x <= S + pad; x += minorGap, vi++) {
      const drift = (rng() - 0.5) * minorGap * 1.2;
      const pts = wavy(x, -pad, x + drift, S + pad, rng, jit, 4);
      (vi % majorEvery === 0 ? roads.major : roads.minor).push(pts);
    }
    // horizontal streets
    let hi = 0;
    for (let y = -pad; y <= S + pad; y += minorGap, hi++) {
      const drift = (rng() - 0.5) * minorGap * 1.2;
      const pts = wavy(-pad, y, S + pad, y + drift, rng, jit, 4);
      (hi % majorEvery === 0 ? roads.major : roads.minor).push(pts);
    }
    // a few diagonal avenues (highways)
    const nDiag = 2 + Math.floor(rng() * 3);
    for (let i = 0; i < nDiag; i++) {
      const x1 = rng() * S, x2 = rng() * S;
      roads.highway.push(wavy(x1, -pad, x2, S + pad, rng, 120, 5));
    }

    // river: enters top-ish, exits bottom/side
    if (rng() > 0.25) {
      const sx = rng() * S, ex = rng() * S;
      rivers.push(wavy(sx, -pad, ex, S + pad, rng, 360, 6));
    }
    // lake near a random corner
    if (rng() > 0.5) {
      const cx = rng() > 0.5 ? S * (0.12 + rng() * 0.12) : S * (0.76 + rng() * 0.12);
      const cy = rng() > 0.5 ? S * (0.12 + rng() * 0.12) : S * (0.76 + rng() * 0.12);
      lakes.push(blob(cx, cy, 180 + rng() * 160, rng, 10));
    }
    // parks
    const nPark = 3 + Math.floor(rng() * 4);
    for (let i = 0; i < nPark; i++) {
      const cx = S * (0.12 + rng() * 0.76);
      const cy = S * (0.12 + rng() * 0.76);
      parks.push(blob(cx, cy, 90 + rng() * 150, rng, 9));
    }

    // fake coordinates
    const lat = (rng() * 130 - 60);
    const lng = (rng() * 340 - 170);
    const coords =
      Math.abs(lat).toFixed(4) + "° " + (lat >= 0 ? "N" : "S") + "  /  " +
      Math.abs(lng).toFixed(4) + "° " + (lng >= 0 ? "E" : "W");

    return { roads, parks, rivers, lakes, rot, coords, size: S };
  }

  function blob(cx, cy, r, rng, n) {
    const pts = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const rr = r * (0.62 + rng() * 0.5);
      pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
    }
    return pts;
  }

  const THEMES = {
    vintage:  { name: "Vintage Paper",  bg: "#ece1cb", water: "#cabf9c", park: "#d8cda6", minor: "#bcab85", major: "#a08c63", highway: "#8a754f", text: "#5b4a30", marker: "#a4492c" },
    terracotta:{ name: "Apricot",       bg: "#f7e8d8", water: "#ecc7a6", park: "#f3d8bd", minor: "#e6b791", major: "#d8945f", highway: "#c2733f", text: "#834420", marker: "#a4492c" },
    sage:     { name: "Sage Field",     bg: "#e5e9dd", water: "#bbc8b3", park: "#ccd6bb", minor: "#b1bda1", major: "#93a47e", highway: "#788a62", text: "#44513a", marker: "#c2673e" },
    blush:    { name: "Honey",          bg: "#f3ead2", water: "#dcc681", park: "#e9d9a4", minor: "#d6c184", major: "#c2a44f", highway: "#a8862f", text: "#6a541c", marker: "#7a4a6e" },
    sky:      { name: "Harbor Blue",    bg: "#dde6ea", water: "#aec6d2", park: "#cad8d4", minor: "#a8bcc4", major: "#85a0ab", highway: "#65838f", text: "#36505b", marker: "#b5502f" },
    midnight: { name: "Midnight Gold",  bg: "#1e2630", water: "#2a3947", park: "#27333f", minor: "#3a4a59", major: "#536881", highway: "#6d8499", text: "#ecdcb5", marker: "#d8a64a" },
    ink:      { name: "Ink & Bone",     bg: "#f4f1ea", water: "#d6d1c6", park: "#e4ded2", minor: "#c3bdb0", major: "#8d877a", highway: "#3d3933", text: "#2a2722", marker: "#b5482e" },
    violet:   { name: "Lavender",       bg: "#ece6f1", water: "#c6b6dd", park: "#dacfe8", minor: "#c2b0d8", major: "#a589c2", highway: "#8767a6", text: "#4c3a66", marker: "#4a57b5" },
    // Aerial photo styles — satellite:true switches TileMap to Esri World
    // Imagery raster tiles; photoFilter (optional) tones the photo. bg/text
    // style the caption band; mapBg is the map-area loading colour.
    aerial:     { name: "Aerial Photo",   satellite: true, mapBg: "#39414a", bg: "#f4f1ea", water: "#31465a", park: "#3e5a3c", minor: "#9aa1a8", major: "#8d877a", highway: "#3d3933", text: "#2a2722", marker: "#e8502f" },
    aerialWarm: { name: "Aerial Vintage", satellite: true, photoFilter: "sepia(.32) saturate(.85) contrast(1.03)", mapBg: "#4a4238", bg: "#f3ead9", water: "#8a7350", park: "#8f8560", minor: "#b0a284", major: "#a08c63", highway: "#6a5638", text: "#5b4a30", marker: "#a4492c" },
    aerialNoir: { name: "Aerial Noir",    satellite: true, photoFilter: "grayscale(1) contrast(1.1) brightness(1.02)", mapBg: "#3c3c3c", bg: "#f4f2ee", water: "#6b6b6b", park: "#8a8a8a", minor: "#a8a8a8", major: "#8d877a", highway: "#3d3933", text: "#2a2722", marker: "#b5482e" },
  };

  // ---------- React poster ----------
  function MapPoster(props) {
    const {
      city, theme, view, paper, labels, markerStyle,
      showWater = true, showParks = true, grain = true,
      titleFont, detail = "rich",
    } = props;
    const e = React.createElement;
    const S = MAP_SIZE;

    // poster geometry
    const aspect = paper.aspect;                 // height / width
    const band = labels.show ? (paper.landscape ? 0.2 : 0.18) : 0;        // bottom text band fraction
    const W = 1000, H = W * aspect;
    const mapH = H * (1 - band);
    const mapAspect = W / mapH;                   // w/h of map window

    // view window in map units
    const vw = view.vw;
    const vh = vw / mapAspect;
    const cx = view.cx, cy = view.cy;
    const vb = `${cx - vw / 2} ${cy - vh / 2} ${vw} ${vh}`;

    const rotDeg = (city.rot * 180) / Math.PI;
    const rotXform = `rotate(${rotDeg} ${S / 2} ${S / 2})`;

    // stroke widths (map units)
    const wMinor = detail === "minimal" ? 0 : 3.4;
    const wMajor = 7;
    const wHwy = 12;
    const wRiver = 30;

    const roadEls = [];
    if (showWater) {
      city.lakes.forEach((p, i) => roadEls.push(e("polygon", { key: "lk" + i, points: toPts(p), fill: theme.water })));
      city.rivers.forEach((p, i) => roadEls.push(e("polyline", { key: "rv" + i, points: toPts(p), fill: "none", stroke: theme.water, strokeWidth: wRiver, strokeLinecap: "round", strokeLinejoin: "round" })));
    }
    if (showParks) city.parks.forEach((p, i) => roadEls.push(e("polygon", { key: "pk" + i, points: toPts(p), fill: theme.park })));
    if (wMinor > 0) city.roads.minor.forEach((p, i) => roadEls.push(e("polyline", { key: "mn" + i, points: toPts(p), fill: "none", stroke: theme.minor, strokeWidth: wMinor, strokeLinecap: "round" })));
    city.roads.major.forEach((p, i) => roadEls.push(e("polyline", { key: "mj" + i, points: toPts(p), fill: "none", stroke: theme.major, strokeWidth: wMajor, strokeLinecap: "round" })));
    city.roads.highway.forEach((p, i) => roadEls.push(e("polyline", { key: "hw" + i, points: toPts(p), fill: "none", stroke: theme.highway, strokeWidth: wHwy, strokeLinecap: "round" })));

    // marker (upright, at map centre)
    let markerEl = null;
    const mk = theme.marker;
    if (markerStyle === "pin") {
      markerEl = e("g", { key: "mk", transform: `translate(${S / 2} ${S / 2})` }, [
        e("path", { key: "p", d: "M0,0 C-15,-28 -20,-36 -20,-50 a20,20 0 1,1 40,0 C20,-36 15,-28 0,0 Z", fill: mk }),
        e("circle", { key: "c", cx: 0, cy: -50, r: 7.5, fill: theme.bg }),
      ]);
    } else if (markerStyle === "dot") {
      markerEl = e("g", { key: "mk", transform: `translate(${S / 2} ${S / 2})` }, [
        e("circle", { key: "o", cx: 0, cy: 0, r: 17, fill: "none", stroke: mk, strokeWidth: 6 }),
        e("circle", { key: "i", cx: 0, cy: 0, r: 6, fill: mk }),
      ]);
    } else if (markerStyle === "heart") {
      markerEl = e("g", { key: "mk", transform: `translate(${S / 2} ${S / 2})` },
        e("path", { d: "M0,-6 C-7,-22 -32,-18 -32,2 C-32,22 -10,30 0,44 C10,30 32,22 32,2 C32,-18 7,-22 0,-6 Z", fill: mk }));
    }

    const grainId = "grain-" + (props.id || "0");

    return e("div", {
      className: "poster", style: {
        width: "100%", height: "100%", display: "flex", flexDirection: "column",
        background: theme.bg, color: theme.text, position: "relative", overflow: "hidden", containerType: "size",
      },
    }, [
      // map area
      e("div", { key: "map", className: "poster-map", style: { flex: "1 1 auto", minHeight: 0, position: "relative" } },
        e("svg", {
          width: "100%", height: "100%", viewBox: vb, preserveAspectRatio: "xMidYMid slice",
          style: { display: "block" },
        }, [
          e("rect", { key: "bg", x: -S, y: -S, width: 3 * S, height: 3 * S, fill: theme.bg }),
          e("g", { key: "rot", transform: rotXform }, roadEls),
          markerEl,
        ])
      ),
      // grain overlay
      grain ? e("svg", {
        key: "gr", width: "100%", height: "100%",
        style: { position: "absolute", inset: 0, pointerEvents: "none", mixBlendMode: "multiply", opacity: 0.06 },
      }, [
        e("filter", { key: "f", id: grainId }, e("feTurbulence", { type: "fractalNoise", baseFrequency: "0.9", numOctaves: 2, stitchTiles: "stitch" })),
        e("rect", { key: "r", width: "100%", height: "100%", filter: `url(#${grainId})` }),
      ]) : null,
      // text band
      labels.show ? e("div", {
        key: "txt", className: "poster-text",
        style: {
          flex: "0 0 auto", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", textAlign: "center",
          padding: "5.5% 6% 6.5%",
          gap: "0.3em",
        },
      }, [
        labels.subtitleTop ? e("div", {
          key: "div", style: { display: "flex", alignItems: "center", gap: "0.9em", width: "62%", color: theme.major },
        }, [
          e("span", { key: "l", style: { flex: 1, height: 1, background: "currentColor", opacity: 0.6 } }),
          e("span", { key: "d", style: { width: 5, height: 5, borderRadius: "50%", background: "currentColor" } }),
          e("span", { key: "r", style: { flex: 1, height: 1, background: "currentColor", opacity: 0.6 } }),
        ]) : null,
        e("div", {
          key: "t", style: {
            fontFamily: titleFont, fontWeight: 600, letterSpacing: labels.spaced ? "0.12em" : "0.02em",
            textTransform: labels.upper ? "uppercase" : "none",
            fontSize: "clamp(13px, " + (paper.landscape ? 5 : 5.4) + "cqh, 72px)", lineHeight: 1.04,
          },
        }, labels.title || "Your Place"),
        labels.subtitle ? e("div", {
          key: "s", style: { fontSize: "clamp(7px,1.9cqh,20px)", letterSpacing: "0.3em", textTransform: "uppercase", opacity: 0.78 },
        }, labels.subtitle) : null,
        labels.showCoords && labels.coords ? e("div", {
          key: "c", style: { fontFamily: "'IBM Plex Mono', monospace", fontSize: "clamp(6px,1.5cqh,13px)", letterSpacing: "0.18em", opacity: 0.62, marginTop: "0.2em" },
        }, labels.coords) : null,
      ]) : null,
    ]);
  }

  // ---------- canvas renderer (poster -> PNG for cart preview / order) ----------
  function drawMarker(ctx, style, theme, x, y) {
    ctx.save(); ctx.translate(x, y);
    const mk = theme.marker;
    if (style === "pin") {
      ctx.fillStyle = mk;
      ctx.beginPath(); ctx.arc(0, -50, 20, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.moveTo(-15, -42); ctx.lineTo(15, -42); ctx.lineTo(0, 0); ctx.closePath(); ctx.fill();
      ctx.fillStyle = theme.bg; ctx.beginPath(); ctx.arc(0, -50, 7.5, 0, Math.PI * 2); ctx.fill();
    } else if (style === "dot") {
      ctx.strokeStyle = mk; ctx.lineWidth = 6; ctx.beginPath(); ctx.arc(0, 0, 17, 0, Math.PI * 2); ctx.stroke();
      ctx.fillStyle = mk; ctx.beginPath(); ctx.arc(0, 0, 6, 0, Math.PI * 2); ctx.fill();
    } else if (style === "heart") {
      ctx.fillStyle = mk; ctx.beginPath();
      ctx.moveTo(0, -6);
      ctx.bezierCurveTo(-7, -22, -32, -18, -32, 2);
      ctx.bezierCurveTo(-32, 22, -10, 30, 0, 44);
      ctx.bezierCurveTo(10, 30, 32, 22, 32, 2);
      ctx.bezierCurveTo(32, -18, 7, -22, 0, -6);
      ctx.closePath(); ctx.fill();
    } else if (style === "house") {
      ctx.fillStyle = mk;
      // roof
      ctx.beginPath(); ctx.moveTo(0, -34); ctx.lineTo(30, -8); ctx.lineTo(-30, -8); ctx.closePath(); ctx.fill();
      // body
      ctx.beginPath(); ctx.rect(-22, -8, 44, 34); ctx.fill();
      // door (knockout to map background)
      ctx.fillStyle = theme.bg; ctx.beginPath(); ctx.rect(-7, 6, 14, 20); ctx.fill();
    }
    ctx.restore();
  }

  function spaced(ctx, txt, cx, cy, sp) {
    try { ctx.letterSpacing = sp + "px"; } catch (e) {}
    ctx.fillText(txt, cx + sp / 2, cy);
    try { ctx.letterSpacing = "0px"; } catch (e) {}
  }

  function drawCaption(ctx, labels, theme, titleFont, CW, bandTop, bandH) {
    const cx = CW / 2;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    let titlePx = Math.round(CW * 0.058);
    let subPx = Math.round(CW * 0.024);
    let coordPx = Math.round(CW * 0.019);
    let gap = CW * 0.02;
    const build = () => {
      const rows = [];
      if (labels.subtitleTop) rows.push(["rule", subPx * 0.5]);
      rows.push(["title", titlePx]);
      if (labels.subtitle) rows.push(["sub", subPx]);
      if (labels.showCoords && labels.coords) rows.push(["coord", coordPx]);
      let total = -gap; rows.forEach((r) => (total += r[1] + gap));
      return { rows, total };
    };
    let { rows, total } = build();
    // Scale the whole caption down if it would overflow the band height (the
    // landscape band is short — without this the coordinates line gets cut off).
    const avail = bandH * 0.84;
    if (total > avail) {
      const k = avail / total;
      titlePx = Math.round(titlePx * k); subPx = Math.round(subPx * k);
      coordPx = Math.round(coordPx * k); gap = gap * k;
      ({ rows, total } = build());
    }
    let cy = bandTop + bandH / 2 - total / 2;
    rows.forEach((r) => {
      cy += r[1] / 2;
      if (r[0] === "rule") {
        const w = CW * 0.16;
        ctx.strokeStyle = theme.major; ctx.globalAlpha = 0.6; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(cx - w, cy); ctx.lineTo(cx - CW * 0.012, cy);
        ctx.moveTo(cx + CW * 0.012, cy); ctx.lineTo(cx + w, cy); ctx.stroke();
        ctx.fillStyle = theme.major; ctx.beginPath(); ctx.arc(cx, cy, 2, 0, Math.PI * 2); ctx.fill();
        ctx.globalAlpha = 1;
      } else if (r[0] === "title") {
        ctx.fillStyle = theme.text; ctx.font = `600 ${r[1]}px ${titleFont}`;
        const txt = labels.upper ? (labels.title || "").toUpperCase() : (labels.title || "");
        spaced(ctx, txt, cx, cy, labels.spaced ? r[1] * 0.12 : r[1] * 0.02);
      } else if (r[0] === "sub") {
        ctx.fillStyle = theme.text; ctx.globalAlpha = 0.78;
        ctx.font = `600 ${r[1]}px 'Work Sans', sans-serif`;
        spaced(ctx, (labels.subtitle || "").toUpperCase(), cx, cy, r[1] * 0.3);
        ctx.globalAlpha = 1;
      } else if (r[0] === "coord") {
        ctx.fillStyle = theme.text; ctx.globalAlpha = 0.62;
        ctx.font = `400 ${r[1]}px 'IBM Plex Mono', monospace`;
        spaced(ctx, labels.coords, cx, cy, r[1] * 0.18);
        ctx.globalAlpha = 1;
      }
      cy += r[1] / 2 + gap;
    });
  }

  function renderPosterCanvas(opts) {
    const { city, theme, view, paper, labels, markerStyle, titleFont, detail = "rich", px = 2, baseW = 760 } = opts;
    const CW = baseW;
    const aspect = paper.aspect;
    const totalH = Math.round(CW * aspect);
    const band = labels.show ? (paper.landscape ? 0.2 : 0.18) : 0;
    const mapH = Math.round(totalH * (1 - band));
    const cv = document.createElement("canvas");
    cv.width = Math.round(CW * px); cv.height = Math.round(totalH * px);
    const ctx = cv.getContext("2d");
    ctx.scale(px, px);
    const Sz = MAP_SIZE;

    ctx.fillStyle = theme.bg; ctx.fillRect(0, 0, CW, totalH);

    ctx.save();
    ctx.beginPath(); ctx.rect(0, 0, CW, mapH); ctx.clip();
    ctx.fillStyle = theme.bg; ctx.fillRect(0, 0, CW, mapH);
    const sc = CW / view.vw;
    ctx.translate(CW / 2, mapH / 2);
    ctx.scale(sc, sc);
    ctx.translate(-view.cx, -view.cy);

    const stroke = (pts, color, w) => {
      if (w <= 0) return;
      ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1])));
      ctx.strokeStyle = color; ctx.lineWidth = w; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.stroke();
    };
    const fill = (pts, color) => {
      ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.closePath();
      ctx.fillStyle = color; ctx.fill();
    };

    ctx.save();
    ctx.translate(Sz / 2, Sz / 2); ctx.rotate(city.rot); ctx.translate(-Sz / 2, -Sz / 2);
    city.lakes.forEach((p) => fill(p, theme.water));
    city.rivers.forEach((p) => stroke(p, theme.water, 30));
    city.parks.forEach((p) => fill(p, theme.park));
    const wMinor = detail === "minimal" ? 0 : 3.4;
    if (wMinor > 0) city.roads.minor.forEach((p) => stroke(p, theme.minor, wMinor));
    city.roads.major.forEach((p) => stroke(p, theme.major, 7));
    city.roads.highway.forEach((p) => stroke(p, theme.highway, 12));
    ctx.restore();

    drawMarker(ctx, markerStyle, theme, Sz / 2, Sz / 2);
    ctx.restore();

    if (labels.show) drawCaption(ctx, labels, theme, titleFont, CW, mapH, totalH - mapH);
    return cv;
  }

  function renderPosterPNG(opts) {
    return renderPosterCanvas(opts).toDataURL("image/png");
  }

  window.MAP = { MAP_SIZE };
  window.generateCity = generateCity;
  window.THEMES = THEMES;
  window.MapPoster = MapPoster;
  window.renderPosterCanvas = renderPosterCanvas;
  window.renderPosterPNG = renderPosterPNG;
  window.drawMarker = drawMarker;
  window.drawCaption = drawCaption;
})();
